// ===========================================
// Streak Counter Component
// ===========================================
// Displays current streak with fire animation

import { Box, Typography, Tooltip } from '@mui/material';
import { keyframes } from '@mui/system';

interface StreakCounterProps {
  currentStreak: number;
  longestStreak: number;
  isActive?: boolean;
  size?: 'small' | 'medium' | 'large';
  showLongest?: boolean;
}

// Fire flicker animation
const flicker = keyframes`
  0%, 100% {
    transform: scale(1) rotate(-2deg);
    opacity: 1;
  }
  25% {
    transform: scale(1.05) rotate(2deg);
    opacity: 0.9;
  }
  50% {
    transform: scale(0.98) rotate(-1deg);
    opacity: 1;
  }
  75% {
    transform: scale(1.02) rotate(1deg);
    opacity: 0.95;
  }
`;

export default function StreakCounter({
  currentStreak,
  longestStreak,
  isActive = true,
  size = 'medium',
  showLongest = true,
}: StreakCounterProps) {
  // Size configurations
  const iconSizes = {
    small: '1.5rem',
    medium: '2rem',
    large: '3rem',
  };

  const numberSizes = {
    small: '1rem',
    medium: '1.5rem',
    large: '2rem',
  };

  const labelSizes = {
    small: '0.625rem',
    medium: '0.75rem',
    large: '0.875rem',
  };

  const hasStreak = currentStreak > 0;
  const isRecord = currentStreak >= longestStreak && hasStreak;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Tooltip
        title={
          hasStreak
            ? `${currentStreak} week${currentStreak !== 1 ? 's' : ''} in a row!${
                isRecord ? ' Personal best!' : ''
              }`
            : 'Attend class every week to build your streak!'
        }
        arrow
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            padding: size === 'large' ? 2 : 1,
            borderRadius: 2,
            backgroundColor: hasStreak ? '#FFF3E0' : '#F5F5F5',
            transition: 'all 0.3s ease',
          }}
        >
          <Box
            sx={{
              fontSize: iconSizes[size],
              animation: hasStreak && isActive ? `${flicker} 1s ease-in-out infinite` : 'none',
              filter: hasStreak ? 'none' : 'grayscale(1)',
              opacity: hasStreak ? 1 : 0.5,
            }}
          >
            {hasStreak ? '🔥' : '💨'}
          </Box>
          <Typography
            sx={{
              fontSize: numberSizes[size],
              fontWeight: 700,
              color: hasStreak ? '#FF5722' : 'text.secondary',
            }}
          >
            {currentStreak}
          </Typography>
        </Box>
      </Tooltip>

      <Typography
        sx={{
          fontSize: labelSizes[size],
          color: 'text.secondary',
          mt: 0.5,
        }}
      >
        {currentStreak === 1 ? 'week' : 'weeks'}
      </Typography>

      {showLongest && longestStreak > 0 && (
        <Typography
          sx={{
            fontSize: labelSizes[size],
            color: isRecord ? '#FF5722' : 'text.secondary',
            fontWeight: isRecord ? 600 : 400,
          }}
        >
          {isRecord ? 'New record!' : `Best: ${longestStreak}`}
        </Typography>
      )}
    </Box>
  );
}
